import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { businessInfoAPI } from '../services/api.js';

// Per-tenant business details the AI uses on every call. One row per tenant —
// this is a single upsert form (no list/CRUD). Mirrors the FAQ/Knowledge card
// styling; placed ABOVE them in Settings because it's the most fundamental
// context the AI needs.

const FIELDS = [
  { key: 'name', label: 'Business name', placeholder: 'e.g. Bacardi Wellness', type: 'input' },
  { key: 'website', label: 'Website', placeholder: 'e.g. https://example.com', type: 'input' },
  { key: 'phone', label: 'Phone', placeholder: 'e.g. (346) 555-0100', type: 'input' },
  { key: 'mainContact', label: 'Main contact', placeholder: 'Who to reach — person or department', type: 'input' },
  { key: 'hoursText', label: 'Hours', placeholder: 'e.g. Mon–Fri 9–5 CT', type: 'textarea' },
  { key: 'address', label: 'Address', placeholder: 'Mailing or physical address', type: 'textarea' },
  { key: 'notes', label: 'Notes', placeholder: 'Anything else the AI should know about your business', type: 'textarea' }
];

const EMPTY = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

function fromServer(data) {
  return FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: data?.[f.key] ?? '' }), {});
}

export default function BusinessInfoEditor() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await businessInfoAPI.get();
      setForm(fromServer(r?.data));
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load business information.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSavedAt(null);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      // Send trimmed values; empty strings become null server-side.
      const payload = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: form[f.key] }), {});
      const r = await businessInfoAPI.update(payload);
      setForm(fromServer(r?.data));
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card">
      <div className="mb-4">
        <h2 className="text-xl text-stone-100">Business information</h2>
        <p className="text-sm text-stone-400 mt-1">
          The AI uses this on every call — name, website, phone, who to reach.
          Fill in as much as you can so it answers callers accurately.
        </p>
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-sm text-stone-300 mb-1">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  className="field-input min-h-[80px]"
                  placeholder={f.placeholder}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              ) : (
                <input
                  className="field-input"
                  placeholder={f.placeholder}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              )}
            </div>
          ))}

          <div className="flex items-center gap-3">
            <button type="button" className="btn-primary" onClick={save} disabled={saving}>
              <Save size={16} />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {savedAt && (
              <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
